import { uploadMeta } from "~/utils/files-cdn"
import { ITokenInfo } from "~/types/global"
import { axiosInstance } from "~/utils/axios"

export interface PresignedUrl {
  url: string
  key: string
  s3path: string
}

export interface ScopedFile {
  id: string
  name?: string
  s3_path: string
  percentage_completion: number
  error?: string | null
}

export async function getPresignedUrls({
  tokenInfo,
  sessionId,
  parentSessionId,
  files
}: {
  tokenInfo: ITokenInfo
  sessionId: string
  parentSessionId?: string
  files: { name: string; type?: string; size?: number }[]
}) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/proposal/rfp/presigned_urls`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    },
    data: {
      files,
      sessionId: parentSessionId ?? "",
      subSessionId: sessionId,
      type: "CHAT_CONTEXT"
    }
  })
}

export function uploadFileToS3({
  url,
  file,
  onProgress,
  signal
}: {
  url: string
  file: File
  onProgress?: (pct: number) => void
  signal?: AbortSignal
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("PUT", url)
    xhr.setRequestHeader(
      "Content-Type",
      file.type || "application/octet-stream"
    )

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100))
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100)
        resolve()
      } else {
        reject(new Error(`S3 upload failed (${xhr.status})`))
      }
    }
    xhr.onerror = () => reject(new Error("S3 upload failed: network error"))
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"))

    if (signal) {
      if (signal.aborted) {
        xhr.abort()
        return
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true })
    }

    xhr.send(file)
  })
}

/**
 * Attach pre-uploaded "My Files" entries (rfp_user_scoped_files) to a chat
 * session so the docs land in the CHAT_CONTEXT OpenSearch indexes
 * ChatDocQnA reads. Closes the symmetry gap with `notifyFilesUploaded`
 * exposed by ENG-1249 / ENG-1298 — picking from My Files used to skip
 * session-scoped ingestion entirely, leaving ChatDocQnA's session_id
 * filter empty so the agent fell through to a placeholder.
 *
 * BE inserts rows in rfp_session_scoped_files(scope=CHAT_CONTEXT) and
 * dispatches the same SQS the fresh-upload path uses. FE polls
 * /scoped_files_status (existing useScopedFilesStatus hook) until
 * percentage_completion=100 before enabling Send.
 */
export interface AttachedExistingFile {
  id: string
  file_id: string
  s3_path: string
  reused: boolean
}

export async function attachExistingFiles({
  tokenInfo,
  sessionId,
  fileIds
}: {
  tokenInfo: ITokenInfo
  sessionId: string
  fileIds: string[]
}) {
  if (!sessionId) {
    return Promise.reject(new Error("attachExistingFiles: sessionId required"))
  }
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/session/${sessionId}/attach_existing_files`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    },
    data: { file_ids: fileIds }
  })
}

export async function notifyFilesUploaded({
  tokenInfo,
  sessionId,
  filesPath,
  showMyFilesInRfpResponse
}: {
  tokenInfo: ITokenInfo
  sessionId: string
  filesPath: string[]
  showMyFilesInRfpResponse?: boolean
}) {
  if (!sessionId) {
    return Promise.reject(new Error("notifyFilesUploaded: sessionId required"))
  }
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/session/${sessionId}/scoped_files_uploaded`
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    },
    data: {
      files: filesPath.map((path) => ({ s3_path: path })),
      ...(showMyFilesInRfpResponse !== undefined && {
        show_my_files_in_rfp_response: showMyFilesInRfpResponse
      })
    }
  })
}

export async function deleteScopedFiles({
  tokenInfo,
  sessionId,
  fileIds
}: {
  tokenInfo: ITokenInfo
  sessionId: string
  fileIds: string[]
}) {
  if (!sessionId || !fileIds.length || !tokenInfo) return
  return await axiosInstance({
    method: "POST",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/session/${sessionId}/delete_scoped_files`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`,
      "Content-Type": "application/json"
    },
    data: {
      file_ids: fileIds
    }
  })
}

/**
 * One-shot helper for the Proactive Proposal entry points: upload reference
 * files through the CHAT_CONTEXT scoped-files pipeline (presigned → S3 →
 * notify) so they land in the OpenSearch indexes ChatDocQnA reads, scoped to
 * `sessionId`. Returns the S3 paths. Mirrors the per-step logic in
 * useAgentFileUpload.addFiles() but without the React state machine, since the
 * proactive drawer/inline form upload imperatively before navigating. Ingestion
 * is async — callers gate the researcher run on getScopedFilesStatus (see
 * agent-search-v2 proactive auto-trigger).
 */
export async function uploadProactiveRefFiles({
  tokenInfo,
  sessionId,
  files
}: {
  tokenInfo: ITokenInfo
  sessionId: string
  files: File[]
}): Promise<string[]> {
  if (!files.length) return []
  const presignedResp = await getPresignedUrls({
    tokenInfo,
    sessionId,
    files: files.map((f) => ({ name: f.name, ...uploadMeta(f) }))
  })
  const presignedUrls: PresignedUrl[] =
    (presignedResp.data as { data?: { presignedUrls?: PresignedUrl[] } })?.data
      ?.presignedUrls ||
    (presignedResp.data as { presignedUrls?: PresignedUrl[] })?.presignedUrls ||
    []
  const filesPath: string[] = []
  for (const signed of presignedUrls) {
    const file = files.find((f) => f.name === signed.key)
    if (!file) continue
    await uploadFileToS3({ url: signed.url, file })
    filesPath.push(signed.s3path)
  }
  if (filesPath.length) {
    await notifyFilesUploaded({ tokenInfo, sessionId, filesPath })
  }
  return filesPath
}

export async function getScopedFilesStatus({
  tokenInfo,
  sessionId,
  showMyFilesInRfpResponse
}: {
  tokenInfo: ITokenInfo
  sessionId: string
  showMyFilesInRfpResponse?: boolean
}) {
  if (!sessionId) {
    return Promise.reject(new Error("getScopedFilesStatus: sessionId required"))
  }
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/session/${sessionId}/scoped_files_status`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    params: {
      ...(showMyFilesInRfpResponse !== undefined && {
        show_my_files_in_rfp_response: showMyFilesInRfpResponse
      })
    }
  })
}
